import React, { useState, useEffect, useCallback, useMemo } from "react";
import { JsonRpc } from "@proton/js";
import {
  Bookmark,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Share2,
  Users,
} from "lucide-react";
import Tooltip from "./Tooltip";
import BuyModal from "./BuyModal";
import MultiOutcomeChart from "./MultiOutcomeChart";
import CommentReactions, {
  ReactionData,
  DEFAULT_REACTIONS,
} from "./CommentReactions";
import Loader from "./loader/Loader";
import {
  normalizeTimestamp,
  getExpiryLabel,
  formatDate,
} from "../utils/dateUtils";
import {
  estimateLmsrBuyQuote,
  estimateLmsrSellPayout,
  type LmsrQuote,
  type LmsrSellEstimate,
} from "../utils/lmsrMath";
import {
  estimateMultiOutcomeBuyQuote,
  estimateMultiOutcomeSellQuote,
  type MultiOutcomeMarketSellEstimate,
} from "../utils/multiOutcomeMarkets";
import {
  buildMultiOutcomeStates,
  estimateBinaryMarketBuyQuote,
  estimateBinaryMarketSellQuote,
  getMultiOutcomeRowPrices,
  resolveLmsrB,
  type BinaryMarketSellEstimate,
} from "../utils/markets";
import {
  clampSellQuantityInput,
  formatSellQuantity,
  stepSellQuantity,
} from "../utils/sellQuantity";
import {
  aggregateMoynVolumeByOutcomeMicro,
  aggregatePoslmsrBinaryDisplayVolumeMicro,
  normalizeMoynStateRows,
  summarizeMoynPositions,
} from "../utils/moyn";
import {
  buyMoynSharesTransaction,
  sellMoynSharesTransaction,
  sellLmsrSharesTransaction,
} from "../utils/crypto";

import { TRADING_TOKEN } from "../configs/config";


interface Order {
  order_id: number;
  account: string;
  outcome_id: number;
  isBid: boolean;
  price: number;
  quantity: number;
}

interface Outcome {
  outcome_id: number;
  name: string;
  display_order: number;
}

interface Comment {
  id: number;
  market_id: number;
  user_account: string;
  comment_text: string;
  created_at: number;
  parent_comment_id: number | null;
  is_deleted: number;
  deleted_by: string | null;
  deleted_at: number | null;
}

interface Activity {
  type: string;
  market_id: number;
  user: string;
  timestamp: string;
  block_num: number;
  trx_id: string;
  outcome_id?: number;
  side?: string;
  price?: string;
  quantity?: number;
  question?: string;
  category?: string;
  outcome?: number;
}

interface MarketDetailProps {
  session: any;
  marketId: number;
  onBack: () => void;
  onSelectRelatedMarket?: (marketId: number) => void;
}

const MarketDetail: React.FC<MarketDetailProps> = ({
  session,
  marketId,
  onBack,
  onSelectRelatedMarket,
}) => {
  const [market, setMarket] = useState<any>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"trade" | "comments" | "activity">(
    "trade",
  );
  const [commentAdmins, setCommentAdmins] = useState<string[]>([]);
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState<string>("");
  const [activityUserFilter, setActivityUserFilter] = useState<string>("");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  // Advanced trading removed - using modal instead
  const [showTradingGuide, setShowTradingGuide] = useState(() => {
    return localStorage.getItem("xpr-trading-guide-dismissed") !== "true";
  });
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyModalOutcome, setBuyModalOutcome] = useState<Outcome | null>(null);
  const [buyModalSide, setBuyModalSide] = useState<"yes" | "no">("yes");
  const [buyQuantity, setBuyQuantity] = useState("");
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellModalOrder, setSellModalOrder] = useState<Order | null>(null);
  const [sellQuantity, setSellQuantity] = useState("");
  const [relatedMarkets, setRelatedMarkets] = useState<any[]>([]);
  const [lmsrPosition, setLmsrPosition] = useState<{
    sharesYes: number;
    sharesNo: number;
  } | null>(null);
  // V3: per-outcome positions { [outcomeId]: { sharesYes, sharesNo } }
  const [lmsrPositionsV3, setLmsrPositionsV3] = useState<Record<
    number,
    { sharesYes: number; sharesNo: number }
  > | null>(null);
  const [multiOutcomeInvested, setMultiOutcomeInvested] = useState<number>(0);
  const [marketMeta, setMarketMeta] = useState<{
    description: string;
    resolution_criteria: string;
  } | null>(null);
  const [participants, setParticipants] = useState<number | null>(null);
  /** V3: per-outcome volume from moynpos (sum yes_cost_basis + no_cost_basis), micro-units */
  const [moynVolumeByOutcomeMicro, setMoynVolumeByOutcomeMicro] = useState<
    Record<number, number>
  >({});
  /** V2 binary: outcome 0 = sum(yes_cost_basis), outcome 1 = sum(no_cost_basis) from poslmsr */
  const [poslmsrBinaryVolumeMicro, setPoslmsrBinaryVolumeMicro] = useState<
    Record<number, number>
  >({});
  const [showLmsrSellModal, setShowLmsrSellModal] = useState(false);
  const [lmsrSellOutcome, setLmsrSellOutcome] = useState<"yes" | "no">("yes");
  const [lmsrSellOutcomeId, setLmsrSellOutcomeId] = useState<number>(0); // V3: which outcome to sell
  const [lmsrSellQuantity, setLmsrSellQuantity] = useState("");
  const [lmsrSellValidation, setLmsrSellValidation] = useState<string | null>(
    null,
  );
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [copiedMarketLink, setCopiedMarketLink] = useState(false);
  const [commentReactions, setCommentReactions] = useState<
    Record<number, Record<string, ReactionData>>
  >({});
  const [userCommentReactions, setUserCommentReactions] = useState<
    Record<number, string | null>
  >({});

  const dismissTradingGuide = () => {
    setShowTradingGuide(false);
    localStorage.setItem("xpr-trading-guide-dismissed", "true");
  };

  const showToast = (
    message: string,
    type: "success" | "error" = "success",
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchMarketData = useCallback(async () => {
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const marketResult = await rpc.get_table_rows({
        code: contractName,
        scope: contractName,
        table: "markets3",
        lower_bound: marketId,
        upper_bound: marketId,
        limit: 1,
      });

      if (marketResult.rows.length > 0) {
        const marketData = marketResult.rows[0];

        // For V3 markets, fetch categorical state from the moynstate table
        let outcomeStates:
          | Array<{
              outcome_id: number;
              q?: number;
              q_yes: number;
              q_no: number;
              volume: number;
            }>
          | undefined;
        if (marketData.version === 3) {
          const moynStateResult = await rpc.get_table_rows({
            code: contractName,
            scope: marketId.toString(),
            table: "moynstate",
            limit: 100,
          });
          outcomeStates = normalizeMoynStateRows(moynStateResult.rows);
        }

        setMarket({
          ...marketData,
          b: resolveLmsrB(marketData.b),
          expireSec: normalizeTimestamp(marketData.expire),
          outcomes_count: marketData.outcomes_count || 2,
          ...(outcomeStates ? { outcome_states: outcomeStates } : {}),
        });

        const outcomesResult = await rpc.get_table_rows({
          code: contractName,
          scope: marketId.toString(),
          table: "outcomes",
          limit: 100,
        });

        const fetchedOutcomes: Outcome[] = outcomesResult.rows.map(
          (row: any) => ({
            outcome_id: row.outcome_id,
            name: row.name,
            display_order: row.display_order,
          }),
        );

        fetchedOutcomes.sort((a, b) => a.display_order - b.display_order);
        setOutcomes(fetchedOutcomes);

        if (fetchedOutcomes.length > 0 && selectedOutcomeId === 0) {
          setSelectedOutcomeId(fetchedOutcomes[0].outcome_id);
        }
      }

      // const ordersResult = await rpc.get_table_rows({
      //   code: contractName,
      //   scope: marketId.toString(),
      //   table: "orders",
      //   limit: 100,
      // });

      // setOrders(ordersResult.rows);

      // Fetch LMSR positions for participant count and user position (for LMSR markets version >= 2)
      if (marketResult.rows.length > 0 && marketResult.rows[0].version >= 2) {
        try {
          const marketVersion = marketResult.rows[0].version;

          if (marketVersion === 3) {
            setPoslmsrBinaryVolumeMicro({});
            const moynPosResult = await rpc.get_table_rows({
              code: contractName,
              scope: marketId.toString(),
              table: "moynpos",
              limit: 1000,
            });
            setMoynVolumeByOutcomeMicro(
              aggregateMoynVolumeByOutcomeMicro(moynPosResult.rows),
            );
            const summary = summarizeMoynPositions(
              moynPosResult.rows,
              session ? session.auth.actor.toString() : null,
              marketResult.rows[0].fee_bps ?? 100,
            );
            setParticipants(summary.participants);
            setLmsrPositionsV3(
              Object.keys(summary.positionsByOutcome).length > 0
                ? summary.positionsByOutcome
                : null,
            );
            setMultiOutcomeInvested(summary.netInvested);
            setLmsrPosition(null);
          } else {
            // V2 binary: query poslmsr table
            setMoynVolumeByOutcomeMicro({});
            const lmsrPosResult = await rpc.get_table_rows({
              code: contractName,
              scope: marketId.toString(),
              table: "poslmsr",
              limit: 1000,
            });

            setPoslmsrBinaryVolumeMicro(
              aggregatePoslmsrBinaryDisplayVolumeMicro(lmsrPosResult.rows),
            );

            setParticipants(lmsrPosResult.rows.length);

            if (session) {
              const userAccount = session.auth.actor.toString();
              const userPosResult = await rpc.get_table_rows({
                code: contractName,
                scope: marketId.toString(),
                table: "poslmsr",
                lower_bound: userAccount,
                upper_bound: userAccount,
                limit: 1,
              });

              if (userPosResult.rows.length > 0) {
                const myLmsrPos = userPosResult.rows[0];
                setLmsrPosition({
                  sharesYes: myLmsrPos.shares_yes / 1_000_000,
                  sharesNo: myLmsrPos.shares_no / 1_000_000,
                });
              } else {
                setLmsrPosition(null);
              }
              setLmsrPositionsV3(null); // Clear V3 positions for V2 markets
              setMultiOutcomeInvested(0);
            }
          }
        } catch (error) {
          console.error("Error fetching LMSR positions:", error);
          setLmsrPosition(null);
          setLmsrPositionsV3(null);
          setMultiOutcomeInvested(0);
          setParticipants(null);
          setMoynVolumeByOutcomeMicro({});
          setPoslmsrBinaryVolumeMicro({});
        }
      } else {
        setParticipants(null);
        setMultiOutcomeInvested(0);
        setMoynVolumeByOutcomeMicro({});
        setPoslmsrBinaryVolumeMicro({});
      }
    } catch (error) {
      console.error("Error fetching market data:", error);
    }
  }, [marketId, selectedOutcomeId, session]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await fetch(`/api/comments.php?market_id=${marketId}`);
      const data = await response.json();
      if (data.success) {
        setComments(data.comments);
        if (data.admins) {
          setCommentAdmins(data.admins);
        }
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  }, [marketId]);

  const fetchCommentReactions = useCallback(async () => {
    try {
      const userAccount = session?.auth?.actor?.toString() || "";
      const url = `/api/reactions.php?market_id=${marketId}${userAccount ? `&user_account=${userAccount}` : ""}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setCommentReactions(data.reactions_by_comment || {});
        setUserCommentReactions(data.user_reactions || {});
      }
    } catch (error) {
      console.error("Error fetching comment reactions:", error);
    }
  }, [marketId, session]);

  const fetchActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      let url = `/api/activity.php?market_id=${marketId}&limit=50`;

      if (activityFilter) {
        url += `&event_type=${activityFilter}`;
      }

      if (activityUserFilter) {
        url += `&user=${activityUserFilter}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setActivities(data.activities);
      }
    } catch (error) {
      console.error("Error fetching activity:", error);
    } finally {
      setActivityLoading(false);
    }
  }, [marketId, activityFilter, activityUserFilter]);

  const fetchRelatedMarkets = useCallback(
    async (category: string) => {
      try {
        const rpc = new JsonRpc(
          process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
        );
        const contractName =
          process.env.REACT_APP_CONTRACT_NAME || "xpredict";

        const result = await rpc.get_table_rows({
          code: contractName,
          scope: contractName,
          table: "markets3",
          limit: 100,
        });

        if (result.rows.length > 0) {
          const related = result.rows
            .filter(
              (m: any) =>
                m.market_id !== marketId &&
                m.category === category &&
                !m.resolved,
            )
            .slice(0, 4);
          setRelatedMarkets(related);
        }
      } catch (error) {
        console.error("Error fetching related markets:", error);
      }
    },
    [marketId],
  );

  const fetchMarketMeta = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/market_meta.php?market_id=${marketId}`,
      );
      const data = await response.json();
      if (data.success && data.data) {
        setMarketMeta({
          description: data.data.description || "",
          resolution_criteria: data.data.resolution_criteria || "",
        });
      }
    } catch (error) {
      console.error("Error fetching market metadata:", error);
    }
  }, [marketId]);

  useEffect(() => {
    setMoynVolumeByOutcomeMicro({});
    setPoslmsrBinaryVolumeMicro({});
    setCopiedMarketLink(false);
  }, [marketId]);

  useEffect(() => {
    const saved = localStorage.getItem("watchlist");
    if (!saved) {
      setIsWatchlisted(false);
      return;
    }

    try {
      const watchlist = JSON.parse(saved) as number[];
      setIsWatchlisted(watchlist.includes(marketId));
    } catch (error) {
      console.error("Error loading watchlist:", error);
      setIsWatchlisted(false);
    }
  }, [marketId]);

  useEffect(() => {
    fetchMarketData();
    fetchComments();
    fetchCommentReactions();
    fetchActivity();
    fetchMarketMeta();
    const marketInterval = setInterval(fetchMarketData, 5000);
    const activityInterval = setInterval(fetchActivity, 30000);
    return () => {
      clearInterval(marketInterval);
      clearInterval(activityInterval);
    };
  }, [
    fetchMarketData,
    fetchComments,
    fetchCommentReactions,
    fetchActivity,
    fetchMarketMeta,
  ]);

  useEffect(() => {
    if (market?.category) {
      fetchRelatedMarkets(market.category);
    }
  }, [market?.category, fetchRelatedMarkets]);

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!session) {
      alert("Please connect your wallet to post a comment");
      return;
    }

    if (!newComment.trim()) {
      alert("Please enter a comment");
      return;
    }

    if (newComment.length > 1000) {
      alert("Comment is too long (max 1000 characters)");
      return;
    }

    setCommentLoading(true);
    try {
      const response = await fetch("/api/comments.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          market_id: marketId,
          user_account: session.auth.actor.toString(),
          comment_text: newComment.trim(),
          parent_comment_id: replyingTo ? replyingTo.id : null,
        }),
      });

      const text = await response.text();
      let data;

      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error("Non-JSON response:", text);
        alert(
          `Failed to post comment: Server returned non-JSON response (${response.status}). Check console for details.`,
        );
        return;
      }

      if (data.success) {
        setNewComment("");
        setReplyingTo(null);
        fetchComments();
      } else {
        alert("Failed to post comment: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      alert("Failed to post comment: " + error);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleReply = (comment: Comment) => {
    if (!session) {
      alert("Please connect your wallet to reply");
      return;
    }

    setReplyingTo(comment);

    const quotedText = comment.comment_text.split("\n")[0].substring(0, 80);
    const suffix = comment.comment_text.length > 80 ? "..." : "";
    setNewComment(`@${comment.user_account}\n${quotedText}${suffix}\n\n`);

    const textarea = document.querySelector(
      ".comment-form textarea",
    ) as HTMLTextAreaElement;
    if (textarea) {
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  };

  const renderCommentText = (comment: Comment) => {
    if (comment.is_deleted) {
      return (
        <div className="comment-text deleted-text">[Moderator Deleted]</div>
      );
    }

    const lines = comment.comment_text.split("\n");
    if (
      comment.parent_comment_id &&
      lines.length > 0 &&
      lines[0].startsWith("@")
    ) {
      const quotedAuthor = lines[0];
      const quotedText = lines.slice(1).join("\n").trim();
      const replyText = quotedText.split("\n\n").slice(1).join("\n\n");
      const quote = quotedText.split("\n\n")[0];

      return (
        <div className="comment-text">
          {quote && (
            <div className="quoted-message">
              <div className="quote-author">{quotedAuthor}</div>
              <div className="quote-text">{quote}</div>
            </div>
          )}
          {replyText && <div className="reply-text">{replyText}</div>}
        </div>
      );
    }

    return <div className="comment-text">{comment.comment_text}</div>;
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!session) {
      alert("Please connect your wallet");
      return;
    }

    const userAccount = session.auth.actor.toString();
    if (!commentAdmins.includes(userAccount)) {
      alert("You do not have permission to delete comments");
      return;
    }

    if (
      !window.confirm(
        'Are you sure you want to delete this comment? It will be replaced with "[Moderator Deleted]".',
      )
    ) {
      return;
    }

    try {
      const response = await fetch("/api/comments.php", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: userAccount,
        }),
      });

      const data = await response.json();

      if (data.success) {
        fetchComments();
      } else {
        alert("Failed to delete comment: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      alert("Failed to delete comment: " + error);
    }
  };

  const outcomeStats = useMemo(() => {
    const stats: Record<
      number,
      { bestBid?: number; bestAsk?: number; volume?: number; noPrice?: number }
    > = {};
    const SCALE = 1_000_000;

    if (market && market.version === 3 && (market as any).outcome_states) {
      const rawOutcomeStates = (market as any).outcome_states as Array<{
        outcome_id: number;
        q?: number;
        q_yes: number;
        q_no: number;
        volume: number;
      }>;
      const outcomeStates = buildMultiOutcomeStates(
        outcomes.map((outcome) => outcome.outcome_id),
        rawOutcomeStates,
      );
      const rowPrices = getMultiOutcomeRowPrices(
        outcomeStates,
        resolveLmsrB(market.b),
      );

      // Version 3 display is categorical: each row shows the normalized YES
      // probability for that outcome, and NO is displayed in the popup as 1-YES.
      // Per-outcome volume: sum(yes_cost_basis + no_cost_basis) from moynpos for that outcome_id.
      outcomes.forEach((outcome) => {
        const volMicro =
          moynVolumeByOutcomeMicro[outcome.outcome_id] ?? 0;
        const volume = volMicro / SCALE;
        const rowPrice = rowPrices[outcome.outcome_id];
        stats[outcome.outcome_id] = {
          bestBid: rowPrice?.yesPrice,
          bestAsk: rowPrice?.yesPrice,
          noPrice: rowPrice?.noPrice,
          volume: volume > 0 ? volume : undefined,
        };
      });
    } else if (market && market.version === 2 && market.b && market.b > 0) {
      // Binary LMSR markets (version == 2) - uses q_yes/q_no
      const b = market.b / SCALE;
      const qYes = (market.q_yes || 0) / SCALE;
      const qNo = (market.q_no || 0) / SCALE;

      // LMSR probability: P(yes) = e^(q_yes/b) / (e^(q_yes/b) + e^(q_no/b))
      const expYes = Math.exp(qYes / b);
      const expNo = Math.exp(qNo / b);
      const total = expYes + expNo;

      const pYes = expYes / total;
      const pNo = expNo / total;

      // Set prices based on LMSR probabilities
      // outcome_id 0 = pYes, outcome_id 1 = pNo
      outcomes.forEach((outcome) => {
        const price = outcome.outcome_id === 0 ? pYes : pNo;
        const volMicro =
          outcome.outcome_id === 0
            ? poslmsrBinaryVolumeMicro[0] ?? 0
            : poslmsrBinaryVolumeMicro[1] ?? 0;
        const volume = volMicro / SCALE;
        stats[outcome.outcome_id] = {
          bestBid: price,
          bestAsk: price,
          volume: volume > 0 ? volume : undefined,
        };
      });
    } 
    // else {
    //   // Legacy order book markets
    //   outcomes.forEach((outcome) => {
    //     const outcomeOrders = orders.filter(
    //       (o) => o.outcome_id === outcome.outcome_id,
    //     );
    //     const outcomeBids = outcomeOrders
    //       .filter((o) => o.isBid)
    //       .sort((a, b) => b.price - a.price);
    //     const outcomeAsks = outcomeOrders
    //       .filter((o) => !o.isBid)
    //       .sort((a, b) => a.price - b.price);

    //     const totalVolume = outcomeOrders.reduce(
    //       (sum, o) => sum + o.price * o.quantity,
    //       0,
    //     );

    //     stats[outcome.outcome_id] = {
    //       bestBid: outcomeBids.length > 0 ? outcomeBids[0].price : undefined,
    //       bestAsk: outcomeAsks.length > 0 ? outcomeAsks[0].price : undefined,
    //       volume: totalVolume,
    //     };
    //   });
    // }

    return stats;
  }, [outcomes, market, moynVolumeByOutcomeMicro, poslmsrBinaryVolumeMicro]);

  const getLmsrStateForTrade = useCallback(
    (
      outcomeId?: number,
    ): { qYes: number; qNo: number; b: number; feeBps: number } | null => {
      if (!market) return null;
      const marketB = resolveLmsrB(market.b);

      if (market.version === 3) {
        const rawOutcomeStates = (market as any).outcome_states as
          | Array<{
              outcome_id: number;
              q?: number;
              q_yes: number;
              q_no: number;
              volume: number;
            }>
          | undefined;
        const outcomeStates = buildMultiOutcomeStates(
          outcomes.map((outcome) => outcome.outcome_id),
          rawOutcomeStates,
        );
        const state = outcomeStates?.find((s) => s.outcome_id === outcomeId);
        if (!state) return null;
        return {
          qYes: state.q_yes || 0,
          qNo: state.q_no || 0,
          b: marketB,
          feeBps: market.fee_bps ?? 100,
        };
      }

      return {
        qYes: market.q_yes || 0,
        qNo: market.q_no || 0,
        b: marketB,
        feeBps: market.fee_bps ?? 100,
      };
    },
    [market, outcomes],
  );

  const lmsrQuote = useMemo<LmsrQuote | null>(() => {
    if (!buyModalOutcome || !buyQuantity) return null;

    const spendAmount = parseInt(buyQuantity, 10);
    if (isNaN(spendAmount) || spendAmount <= 0) return null;

    const state = getLmsrStateForTrade(buyModalOutcome.outcome_id);
    if (market?.version === 3) {
      const rawOutcomeStates = (market as any).outcome_states as
        | Array<{
            outcome_id: number;
            q?: number;
            q_yes: number;
            q_no: number;
            volume: number;
          }>
        | undefined;
      const outcomeStates = buildMultiOutcomeStates(
        outcomes.map((outcome) => outcome.outcome_id),
        rawOutcomeStates,
      );
      return estimateMultiOutcomeBuyQuote(
        outcomeStates.map((outcomeState) => outcomeState.q ?? outcomeState.q_yes ?? 0),
        resolveLmsrB(market.b),
        buyModalOutcome.outcome_id,
        buyModalSide,
        spendAmount,
        market.fee_bps ?? 100,
      );
    }

    if (!state) return null;

    if (market?.version === 2) {
      return estimateBinaryMarketBuyQuote(
        state.qYes,
        state.qNo,
        state.b,
        buyModalSide,
        spendAmount,
        state.feeBps,
      );
    }

    return estimateLmsrBuyQuote(
      state.qYes,
      state.qNo,
      state.b,
      buyModalSide,
      spendAmount,
      state.feeBps,
    );
  }, [
    buyModalOutcome,
    buyModalSide,
    buyQuantity,
    getLmsrStateForTrade,
    market,
    outcomes,
  ]);

  const lmsrSellEstimate = useMemo<
    LmsrSellEstimate | BinaryMarketSellEstimate | MultiOutcomeMarketSellEstimate | null
  >(() => {
    if (!lmsrSellQuantity) return null;

    const sharesAmount = parseFloat(lmsrSellQuantity);
    if (isNaN(sharesAmount) || sharesAmount <= 0) return null;

    if (market?.version === 3) {
      const rawOutcomeStates = (market as any).outcome_states as
        | Array<{
            outcome_id: number;
            q?: number;
            q_yes: number;
            q_no: number;
            volume: number;
          }>
        | undefined;
      const outcomeStates = buildMultiOutcomeStates(
        outcomes.map((outcome) => outcome.outcome_id),
        rawOutcomeStates,
      );

      return estimateMultiOutcomeSellQuote(
        outcomeStates.map(
          (outcomeState) => outcomeState.q ?? outcomeState.q_yes ?? 0,
        ),
        resolveLmsrB(market.b),
        lmsrSellOutcomeId,
        lmsrSellOutcome,
        sharesAmount,
        market.fee_bps ?? 100,
      );
    }

    const state = getLmsrStateForTrade(lmsrSellOutcomeId);
    if (!state) return null;

    if (market?.version === 2) {
      return estimateBinaryMarketSellQuote(
        state.qYes,
        state.qNo,
        state.b,
        lmsrSellOutcome,
        sharesAmount,
        state.feeBps,
      );
    }

    return estimateLmsrSellPayout(
      state.qYes,
      state.qNo,
      state.b,
      lmsrSellOutcome,
      sharesAmount,
    );
  }, [
    getLmsrStateForTrade,
    market,
    outcomes,
    lmsrSellOutcome,
    lmsrSellOutcomeId,
    lmsrSellQuantity,
  ]);

  const handleOutcomeButtonClick = (outcomeId: number, side: "yes" | "no") => {
    const outcome = outcomes.find((o) => o.outcome_id === outcomeId);
    if (!outcome) return;

    setBuyModalOutcome(outcome);
    setBuyModalSide(side);
    setBuyQuantity("");
    setShowBuyModal(true);
  };

  // For binary markets with single-button mode, clicking "Buy" on a row buys shares for that outcome
  const handleBuyForOutcome = (outcomeId: number) => {
    const outcome = outcomes.find((o) => o.outcome_id === outcomeId);
    if (!outcome) return;

    // Determine which side based on the outcome_id (0 = yes, 1 = no)
    const side: "yes" | "no" = outcome.outcome_id === 0 ? "yes" : "no";

    setBuyModalOutcome(outcome);
    setBuyModalSide(side);
    setBuyQuantity("");
    setShowBuyModal(true);
  };

  const getMarketUrl = useCallback(
    () => `${window.location.origin}/market/${marketId}`,
    [marketId],
  );

  const handleShareMarket = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getMarketUrl());
      setCopiedMarketLink(true);
      showToast("Market link copied", "success");
      setTimeout(() => setCopiedMarketLink(false), 2000);
    } catch (error) {
      console.error("Error copying market link:", error);
      showToast("Failed to copy market link", "error");
    }
  }, [getMarketUrl]);

  const handleToggleWatchlist = useCallback(() => {
    let watchlist: number[] = [];
    const saved = localStorage.getItem("watchlist");

    if (saved) {
      try {
        watchlist = JSON.parse(saved) as number[];
      } catch (error) {
        console.error("Error loading watchlist:", error);
      }
    }

    const nextWatchlist = watchlist.includes(marketId)
      ? watchlist.filter((id) => id !== marketId)
      : [...watchlist, marketId];

    localStorage.setItem("watchlist", JSON.stringify(nextWatchlist));
    setIsWatchlisted(nextWatchlist.includes(marketId));
    showToast(
      nextWatchlist.includes(marketId)
        ? "Added to watchlist"
        : "Removed from watchlist",
      "success",
    );
  }, [marketId]);

  const handleModalBuy = async () => {
    if (!session || !buyModalOutcome || !buyQuantity) {
      return;
    }

    const spendAmount = parseInt(buyQuantity);
    if (isNaN(spendAmount) || spendAmount <= 0) {
      showToast("Please enter a valid amount", "error");
      return;
    }

    // Block the trade if it exceeds the liquidity cap
    if (lmsrQuote?.exceeds_liquidity_cap) {
      const maxAllowed =
        lmsrQuote.max_spend_amount !== undefined
          ? `${lmsrQuote.max_spend_amount.toFixed(2)} ${TRADING_TOKEN}`
          : "N/A";
      showToast(
        `Please reduce the spend. You are not allowed to purchase more than the maximum allowed (${maxAllowed}).`,
        "error",
      );
      return;
    }

    setLoading(true);
    try {
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";
      if (market && market.version === 3) {
        await buyMoynSharesTransaction(session, {
          from: session.auth.actor.toString(),
          to: contractName,
          quantity: `${spendAmount.toFixed(6)} ${TRADING_TOKEN}`,
          market_id: marketId,
          outcome_id: buyModalOutcome.outcome_id,
          side: buyModalSide,
        });
      } else {
        const minShares = lmsrQuote?.min_shares_for_slippage ?? 0;
        await session.transact({
          actions: [
            {
              account: process.env.REACT_APP_TOKEN_CONTRACT || "xtokens",
              name: "transfer",
              authorization: [
                {
                  actor: session.auth.actor,
                  permission: session.auth.permission,
                },
              ],
              data: {
                from: session.auth.actor,
                to: contractName,
                quantity: `${spendAmount.toFixed(6)} ${TRADING_TOKEN}`,
                memo: `buy:${marketId}:${buyModalSide}:${minShares}`,
              },
            },
          ],
        });
      }

      const estimatedShares =
        lmsrQuote?.estimated_shares?.toFixed(2) || spendAmount;
      const boughtOutcomeName =
        buyModalSide === "yes"
          ? outcomes.find((o) => o.outcome_id === 0)?.name || "YES"
          : outcomes.find((o) => o.outcome_id === 1)?.name || "NO";
      showToast(
        `Bought ~${estimatedShares} ${boughtOutcomeName} shares of "${buyModalOutcome.name}" for ${spendAmount} ${TRADING_TOKEN}`,
        "success",
      );
      setShowBuyModal(false);
      setBuyQuantity("");
      fetchMarketData();
    } catch (error) {
      console.error("Error placing LMSR buy:", error);
      showToast("Failed to buy shares: " + error, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSellModal = (order: Order) => {
    setSellModalOrder(order);
    setSellQuantity(order.quantity.toString());
    setShowSellModal(true);
  };

  const handleSellPercentage = (percentage: number) => {
    if (!sellModalOrder) return;
    const amount = Math.floor(sellModalOrder.quantity * (percentage / 100));
    setSellQuantity(amount > 0 ? amount.toString() : "1");
  };

  const handleModalSell = async () => {
    if (!session || !sellModalOrder || !sellQuantity) {
      return;
    }

    const quantityInt = parseInt(sellQuantity);
    if (isNaN(quantityInt) || quantityInt <= 0) {
      showToast("Please enter a valid quantity", "error");
      return;
    }

    if (quantityInt > sellModalOrder.quantity) {
      showToast(
        `Cannot sell more than ${sellModalOrder.quantity} shares`,
        "error",
      );
      return;
    }

    setLoading(true);
    try {
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const actions: any[] = [];

      actions.push({
        account: contractName,
        name: "placeorder",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: {
          account: session.auth.actor,
          market_id: marketId,
          outcome_id: sellModalOrder.outcome_id,
          bid: false,
          price: `${sellModalOrder.price} ${TRADING_TOKEN}`,
          quantity: quantityInt,
        },
      });

      await session.transact({ actions });

      const outcomeName =
        outcomes.find((o) => o.outcome_id === sellModalOrder.outcome_id)
          ?.name || `Outcome ${sellModalOrder.outcome_id}`;
      showToast(
        `Sell order placed: ${quantityInt} shares of "${outcomeName}" at ${sellModalOrder.price} ${TRADING_TOKEN}`,
        "success",
      );
      setShowSellModal(false);
      setSellQuantity("");
      setSellModalOrder(null);
      fetchMarketData();
    } catch (error) {
      console.error("Error placing sell order:", error);
      showToast("Failed to place sell order: " + error, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLmsrSellModal = (
    outcome: "yes" | "no",
    outcomeId?: number,
  ) => {
    setLmsrSellOutcome(outcome);
    if (outcomeId !== undefined || market?.version !== 3) {
      setLmsrSellOutcomeId(outcomeId ?? (outcome === "yes" ? 0 : 1));
    }
    setLmsrSellQuantity("");
    setLmsrSellValidation(null);
    setShowLmsrSellModal(true);
  };

  const closeLmsrSellModal = () => {
    setShowLmsrSellModal(false);
    setLmsrSellQuantity("");
    setLmsrSellValidation(null);
  };

  const getCurrentLmsrSellMaxShares = useCallback((): number => {
    if (market?.version === 3 && lmsrPositionsV3) {
      const pos = lmsrPositionsV3[lmsrSellOutcomeId];
      if (pos) {
        return lmsrSellOutcome === "yes" ? pos.sharesYes : pos.sharesNo;
      }
      return 0;
    }

    if (lmsrPosition) {
      return lmsrSellOutcome === "yes"
        ? lmsrPosition.sharesYes
        : lmsrPosition.sharesNo;
    }

    return 0;
  }, [
    lmsrPosition,
    lmsrPositionsV3,
    lmsrSellOutcome,
    lmsrSellOutcomeId,
    market?.version,
  ]);

  const handleLmsrSellQuantityChange = (nextValue: string) => {
    if (!nextValue) {
      setLmsrSellQuantity("");
      setLmsrSellValidation(null);
      return;
    }

    const maxShares = getCurrentLmsrSellMaxShares();
    const normalized = clampSellQuantityInput(nextValue, maxShares);
    setLmsrSellQuantity(normalized.value);
    setLmsrSellValidation(
      normalized.wasClamped
        ? `Maximum available is ${formatSellQuantity(maxShares)} shares.`
        : null,
    );
  };

  const handleLmsrSellStep = (direction: "up" | "down") => {
    const maxShares = getCurrentLmsrSellMaxShares();
    const nextValue = stepSellQuantity(lmsrSellQuantity, maxShares, direction);
    setLmsrSellQuantity(nextValue);

    if (direction === "up" && nextValue === formatSellQuantity(maxShares)) {
      setLmsrSellValidation(
        maxShares > 0
          ? `Maximum available is ${formatSellQuantity(maxShares)} shares.`
          : null,
      );
      return;
    }

    setLmsrSellValidation(null);
  };

  const handleLmsrSellPercentage = (percentage: number) => {
    const maxShares = getCurrentLmsrSellMaxShares();
    if (maxShares <= 0) return;
    // Calculate fractional amount based on percentage
    const amount = maxShares * (percentage / 100);
    // Round to 6 decimal places to match SCALE precision
    const roundedAmount = Math.round(amount * 1_000_000) / 1_000_000;
    setLmsrSellQuantity(
      roundedAmount > 0 ? roundedAmount.toString() : "0.000001",
    );
    setLmsrSellValidation(null);
  };

  const handleLmsrSell = async () => {
    const isV3 = market && market.version === 3;
    if (!session || (!isV3 && !lmsrPosition) || (isV3 && !lmsrPositionsV3)) {
      return;
    }

    // Parse as float to support fractional shares
    const quantityFloat = parseFloat(lmsrSellQuantity);
    if (isNaN(quantityFloat) || quantityFloat <= 0) {
      showToast("Please enter a valid quantity", "error");
      return;
    }

    const maxShares = getCurrentLmsrSellMaxShares();
    if (quantityFloat > maxShares + 0.000001) {
      // Small epsilon for floating point comparison
      showToast(
        `Cannot sell more than ${maxShares.toFixed(6)} shares`,
        "error",
      );
      return;
    }

    // Convert to fixed-point units (multiply by SCALE = 1_000_000)
    // This allows selling fractional shares (e.g., 0.5 shares = 500_000)
    const sharesScaled = Math.round(quantityFloat * 1_000_000);
    if (sharesScaled <= 0) {
      showToast("Quantity too small", "error");
      return;
    }

    setLoading(true);
    try {
      if (isV3) {
        await sellMoynSharesTransaction(session, {
          user: session.auth.actor.toString(),
          market_id: marketId,
          outcome_id: lmsrSellOutcomeId,
          side_is_yes: lmsrSellOutcome === "yes",
          shares_micro: sharesScaled,
          min_payout_micro: 0,
        });
      } else {

        console.log("Selling LMSR shares with parameters:", {
          user: session.auth.actor.toString(),
          market_id: marketId,
          outcome_is_yes: lmsrSellOutcome === "yes",
          shares_micro: sharesScaled,
          min_payout_micro: 0,
        });

        await sellLmsrSharesTransaction(session, {
          user: session.auth.actor.toString(),
          market_id: marketId,
          outcome_is_yes: lmsrSellOutcome === "yes",
          shares_micro: sharesScaled,
          min_payout_micro: 0,
        });
      }

      let outcomeName: string;
      if (isV3) {
        const outcomeObj = outcomes.find(
          (o) => o.outcome_id === lmsrSellOutcomeId,
        );
        outcomeName = `${outcomeObj?.name || `Outcome ${lmsrSellOutcomeId}`} ${lmsrSellOutcome.toUpperCase()}`;
      } else {
        outcomeName =
          lmsrSellOutcome === "yes"
            ? outcomes.find((o) => o.outcome_id === 0)?.name || "Yes"
            : outcomes.find((o) => o.outcome_id === 1)?.name || "No";
      }
      showToast(`Sold ${quantityFloat} ${outcomeName} shares`, "success");
      closeLmsrSellModal();
      fetchMarketData();
    } catch (error) {
      console.error("Error selling LMSR shares:", error);
      showToast("Failed to sell shares: " + error, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!market) {
    return <Loader label="Loading market..." />;
  }

  // Check if market has expired (current time >= expiry time)
  // Contract enforces this for both buy and sell actions
  const isExpired =
    market.expireSec && Math.floor(Date.now() / 1000) >= market.expireSec;

  const filteredOrders = orders.filter(
    (o) => o.outcome_id === selectedOutcomeId,
  );
  const bids = filteredOrders
    .filter((o) => o.isBid)
    .sort((a, b) => b.price - a.price);
  const asks = filteredOrders
    .filter((o) => !o.isBid)
    .sort((a, b) => a.price - b.price);

  const myOrders = session
    ? orders.filter((o) => o.account === session.auth.actor.toString())
    : [];
  const myOrdersByOutcome = myOrders.reduce(
    (acc, order) => {
      if (!acc[order.outcome_id]) {
        acc[order.outcome_id] = [];
      }
      acc[order.outcome_id].push(order);
      return acc;
    },
    {} as Record<number, Order[]>,
  );

  const sortedOutcomes = [...outcomes].sort(
    (a, b) => a.display_order - b.display_order,
  );
  const marketStatus = market.resolved
    ? "resolved"
    : isExpired
      ? "expired"
      : "active";
  const marketStatusLabel =
    marketStatus === "resolved"
      ? "Resolved"
      : marketStatus === "expired"
        ? "Expired"
        : "Active";
  const resolvedOutcomeName =
    market.resolved && outcomes.length > 0
      ? outcomes.find((outcome) => outcome.outcome_id === market.outcome)
          ?.name || "Unknown"
      : null;

  const getOutcomePrice = (outcomeId: number): number => {
    const stats = outcomeStats[outcomeId] || {};
    return stats.bestAsk ?? stats.bestBid ?? 0;
  };

  const formatOutcomePrice = (price: number): string => {
    if (price <= 0) return "--";
    return price <= 1
      ? `${Math.round(price * 100)}%`
      : `${Math.round(price)} ${TRADING_TOKEN}`;
  };

  const getOutcomeProbability = (outcomeId: number): number => {
    const price = getOutcomePrice(outcomeId);
    if (price <= 0) return 0;
    return Math.max(1, Math.min(100, price <= 1 ? price * 100 : price));
  };

  const getSellSharesForOutcome = (
    outcomeId: number,
    side: "yes" | "no",
  ): number => {
    if (market?.version === 3) {
      const position = lmsrPositionsV3?.[outcomeId];
      if (!position) return 0;
      return side === "yes" ? position.sharesYes : position.sharesNo;
    }

    if (!lmsrPosition) return 0;
    return outcomeId === 0 ? lmsrPosition.sharesYes : lmsrPosition.sharesNo;
  };

  const handleCancelOrder = async (orderId: number) => {
    if (!session) return;

    try {
      await session.transact({
        actions: [
          {
            account: process.env.REACT_APP_CONTRACT_NAME || "xpredict",
            name: "cancelorder",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {
              account: session.auth.actor,
              market_id: marketId,
              order_id: orderId,
            },
          },
        ],
      });

      showToast("Order cancelled successfully!", "success");
      fetchMarketData();
    } catch (error) {
      console.error("Error cancelling order:", error);
      showToast("Failed to cancel order: " + error, "error");
    }
  };

  return (
    <div className="market-detail">
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>
            ×
          </button>
        </div>
      )}

      <BuyModal
        isOpen={showBuyModal}
        outcome={buyModalOutcome}
        side={buyModalSide}
        outcomes={outcomes}
        marketVersion={market?.version}
        quantity={buyQuantity}
        quote={lmsrQuote}
        currentRowPrices={
          buyModalOutcome
            ? {
                yesPrice:
                  outcomeStats[buyModalOutcome.outcome_id]?.bestAsk ??
                  outcomeStats[buyModalOutcome.outcome_id]?.bestBid ??
                  0,
                noPrice:
                  outcomeStats[buyModalOutcome.outcome_id]?.noPrice ?? 0,
              }
            : null
        }
        loading={loading}
        isConfirmDisabled={
          loading ||
          !buyQuantity ||
          parseInt(buyQuantity, 10) <= 0 ||
          lmsrQuote?.exceeds_liquidity_cap === true
        }
        onClose={() => setShowBuyModal(false)}
        onConfirm={handleModalBuy}
        onQuantityChange={setBuyQuantity}
      />

      {showSellModal && sellModalOrder && (
        <div
          className="sell-modal-overlay"
          onClick={() => setShowSellModal(false)}
        >
          <div className="sell-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sell-modal-header">
              <h3>Sell Position</h3>
              <button
                className="sell-modal-close"
                onClick={() => setShowSellModal(false)}
              >
                ×
              </button>
            </div>
            <div className="sell-modal-content">
              <div className="sell-modal-info">
                <div className="sell-info-row">
                  <span className="sell-info-label">Outcome:</span>
                  <span className="sell-info-value">
                    {outcomes.find(
                      (o) => o.outcome_id === sellModalOrder.outcome_id,
                    )?.name || `Outcome ${sellModalOrder.outcome_id}`}
                  </span>
                </div>
                <div className="sell-info-row">
                  <span className="sell-info-label">Current Price:</span>
                  <span className="sell-info-value">
                    {sellModalOrder.price} ${TRADING_TOKEN}
                  </span>
                </div>
                <div className="sell-info-row">
                  <span className="sell-info-label">Available to Sell:</span>
                  <span className="sell-info-value">
                    {sellModalOrder.quantity} shares
                  </span>
                </div>
              </div>
              <div className="sell-modal-form">
                <label>
                  Quantity to Sell
                  <input
                    type="number"
                    min="1"
                    max={sellModalOrder.quantity}
                    step="1"
                    value={sellQuantity}
                    onChange={(e) => setSellQuantity(e.target.value)}
                    placeholder="Enter quantity"
                    autoFocus
                  />
                </label>
                <div className="sell-percentage-buttons">
                  <button
                    type="button"
                    onClick={() => handleSellPercentage(25)}
                  >
                    25%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSellPercentage(50)}
                  >
                    50%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSellPercentage(75)}
                  >
                    75%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSellPercentage(100)}
                  >
                    100%
                  </button>
                </div>
                {sellQuantity && parseInt(sellQuantity) > 0 && (
                  <p className="sell-modal-summary">
                    You will sell <strong>{parseInt(sellQuantity)}</strong>{" "}
                    shares at <strong>{sellModalOrder.price} ${TRADING_TOKEN}</strong>{" "}
                    each.
                    <br />
                    Total:{" "}
                    <strong>
                      {parseInt(sellQuantity) * sellModalOrder.price} ${TRADING_TOKEN}
                    </strong>
                  </p>
                )}
              </div>
            </div>
            <div className="sell-modal-actions">
              <button
                className="sell-modal-cancel"
                onClick={() => setShowSellModal(false)}
              >
                Cancel
              </button>
              <button
                className="sell-modal-confirm"
                onClick={handleModalSell}
                disabled={
                  loading ||
                  !sellQuantity ||
                  parseInt(sellQuantity) <= 0 ||
                  parseInt(sellQuantity) > sellModalOrder.quantity
                }
              >
                {loading ? "Placing Order..." : "Sell"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLmsrSellModal &&
        (() => {
          // Compute sell modal values for both V2 and V3
          const isV3Market = market && market.version === 3;
          let sellOutcomeName = "";
          let sellMaxShares = 0;
          if (isV3Market && lmsrPositionsV3) {
            const pos = lmsrPositionsV3[lmsrSellOutcomeId];
            sellMaxShares = pos
              ? lmsrSellOutcome === "yes"
                ? pos.sharesYes
                : pos.sharesNo
              : 0;
            const outcomeObj = outcomes.find(
              (o) => o.outcome_id === lmsrSellOutcomeId,
            );
            sellOutcomeName = `${outcomeObj?.name || `Outcome ${lmsrSellOutcomeId}`} ${lmsrSellOutcome.toUpperCase()}`;
          } else if (lmsrPosition) {
            sellMaxShares =
              lmsrSellOutcome === "yes"
                ? lmsrPosition.sharesYes
                : lmsrPosition.sharesNo;
            sellOutcomeName =
              lmsrSellOutcome === "yes"
                ? outcomes.find((o) => o.outcome_id === 0)?.name || "Yes"
                : outcomes.find((o) => o.outcome_id === 1)?.name || "No";
          }
          if (sellMaxShares <= 0 && !isV3Market && !lmsrPosition) return null;
          return (
            <div
              className="sell-modal-overlay"
              onClick={closeLmsrSellModal}
            >
              <div className="sell-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sell-modal-header">
                  <h3>Sell {sellOutcomeName} Shares</h3>
                  <button
                    className="sell-modal-close"
                    onClick={closeLmsrSellModal}
                  >
                    ×
                  </button>
                </div>
                <div className="sell-modal-content">
                  <div className="sell-modal-info">
                    <div className="sell-info-row">
                      <span className="sell-info-label">Position:</span>
                      <span className="sell-info-value">{sellOutcomeName}</span>
                    </div>
                    <div className="sell-info-row">
                      <span className="sell-info-label">
                        Available to Sell:
                      </span>
                      <span className="sell-info-value">
                        {sellMaxShares.toFixed(2)} shares
                      </span>
                    </div>
                  </div>
                  <div className="sell-modal-form">
                    <label>
                      Shares to Sell
                      <div className="sell-quantity-input-shell">
                        <input
                          className="sell-quantity-input"
                          type="number"
                          min="0.000001"
                          max={sellMaxShares}
                          step="0.000001"
                          value={lmsrSellQuantity}
                          onChange={(e) =>
                            handleLmsrSellQuantityChange(e.target.value)
                          }
                          placeholder="Enter quantity (e.g., 0.5)"
                          autoFocus
                        />
                        <div className="sell-quantity-stepper">
                          <button
                            type="button"
                            className="sell-quantity-stepper-button"
                            aria-label="Increase shares to sell"
                            onClick={() => handleLmsrSellStep("up")}
                            disabled={
                              sellMaxShares <= 0 ||
                              lmsrSellQuantity ===
                                formatSellQuantity(sellMaxShares)
                            }
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            className="sell-quantity-stepper-button"
                            aria-label="Decrease shares to sell"
                            onClick={() => handleLmsrSellStep("down")}
                            disabled={!lmsrSellQuantity}
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      </div>
                    </label>
                    {lmsrSellValidation && (
                      <p className="sell-modal-validation">
                        {lmsrSellValidation}
                      </p>
                    )}
                    <div className="sell-percentage-buttons">
                      <button
                        type="button"
                        onClick={() => handleLmsrSellPercentage(25)}
                      >
                        25%
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLmsrSellPercentage(50)}
                      >
                        50%
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLmsrSellPercentage(75)}
                      >
                        75%
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLmsrSellPercentage(100)}
                      >
                        100%
                      </button>
                    </div>
                    {lmsrSellQuantity && parseFloat(lmsrSellQuantity) > 0 && (
                      <p className="sell-modal-summary">
                        You will sell{" "}
                        <strong>{parseFloat(lmsrSellQuantity)}</strong>{" "}
                        {sellOutcomeName} shares.
                        <br />
                        {lmsrSellEstimate ? (
                          <>
                            Estimated payout:{" "}
                            <strong className="sell-modal-payout-value">
                              ≈ {lmsrSellEstimate.payout.toFixed(4)} ${TRADING_TOKEN}
                            </strong>
                            .
                            <br />
                            <span style={{ fontSize: "0.85em", color: "#aaa" }}>
                              New odds after sale: YES{" "}
                              {lmsrSellEstimate.new_odds_after_sale.yes.toFixed(
                                1,
                              )}
                              % / NO{" "}
                              {lmsrSellEstimate.new_odds_after_sale.no.toFixed(
                                1,
                              )}
                              %.
                            </span>
                          </>
                        ) : (
                          <span style={{ fontSize: "0.85em", color: "#aaa" }}>
                            Payout is calculated using LMSR pricing and sent
                            directly to your wallet.
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sell-modal-actions">
                  <button
                    className="sell-modal-cancel"
                    onClick={closeLmsrSellModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="sell-modal-confirm"
                    onClick={handleLmsrSell}
                    disabled={
                      loading ||
                      !lmsrSellQuantity ||
                      parseFloat(lmsrSellQuantity) <= 0 ||
                      parseFloat(lmsrSellQuantity) > sellMaxShares + 0.000001
                    }
                  >
                    {loading ? "Selling..." : "Sell Shares"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      <button onClick={onBack} className="back-button">
        ← Back to Markets
      </button>

      <div className="market-detail-layout">
        <div className="market-detail-column market-detail-left-column">
          <div className="market-details-section">
            <div className="market-details-main">
              {market.image_url && (
                <div className="market-detail-image">
                  <img
                    src={market.image_url}
                    alt={market.question}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="market-details-content">
                <div className="market-detail-kicker">
                  <span className="category">{market.category}</span>
                </div>
                <h2>{market.question}</h2>
              </div>
            </div>
            <p className={`market-description-text ${!marketMeta?.description ? "placeholder" : ""}`} >
              {marketMeta?.description ||
                "Description will be provided by the market creator."}
            </p>
          </div>
        </div>
        <div className="market-detail-column market-detail-right-column" style={{ display: "flex", flexDirection: "column", height: "100%"}}>
          <div className="market-share-section">
            <div className="market-detail-action-buttons">
              <button
                className={`market-detail-action-button ${copiedMarketLink ? "copied" : ""}`}
                onClick={handleShareMarket}
                type="button"
              >
                {copiedMarketLink ? <Check size={18} /> : <Share2 size={18} />}
                {copiedMarketLink ? "Copied" : "Share"}
              </button>
              <button
                className={`market-detail-action-button watchlist-button ${isWatchlisted ? "active" : ""}`}
                onClick={handleToggleWatchlist}
                type="button"
                aria-pressed={isWatchlisted}
              >
                <Bookmark
                  size={18}
                  fill={isWatchlisted ? "currentColor" : "none"}
                />
                {isWatchlisted ? "Watching" : "Watchlist"}
              </button>
            </div>
          </div>
        </div>
        <div className="market-detail-column market-detail-left-column">

          {/* {outcomes.length > 0 && (
            <section className="market-detail-card market-price-history-section">
              <h3>Price History</h3>
              <MultiOutcomeChart
                marketId={marketId}
                outcomes={outcomes}
                market={market}
              />
            </section>
          )} */}

          <section className="market-detail-card market-description-section">
            <h3>How this market will be resolved</h3>
            <p
              className={`market-resolution-text ${!marketMeta?.resolution_criteria ? "placeholder" : ""}`}
            >
              {marketMeta?.resolution_criteria ||
                "Resolution criteria will be provided by the market creator."}
            </p>
          </section>

          <section className="market-detail-card market-status-section">
            <div>
              <p className="market-section-eyebrow">Market Status</p>
            </div>
            <div className="market-status-options">
              {(["active", "expired", "resolved"] as const).map((status) => (
                <span
                  key={status}
                  className={`market-status-option ${status} ${marketStatus === status ? "selected" : ""}`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              ))}
            </div>
            {participants !== null && (
                    <span className="market-participants-detail">
                      <Users size={16} strokeWidth={2.25} />
                      {participants}{" "}
                      {participants === 1 ? "trader" : "traders"}
                    </span>
                  )}
            <div className="market-status-meta">
              <span>
                {getExpiryLabel(market.resolved, market.expireSec)}:{" "}
                {formatDate(market.expireSec, true)}
              </span>
              {resolvedOutcomeName && (
                <span>Outcome: {resolvedOutcomeName}</span>
              )}
            </div>
          </section>
        </div>

        <div className="market-detail-column market-detail-right-column">

          <section className="market-detail-card market-actions-section">
            <div className="market-actions-heading">
              <p className="market-section-eyebrow">Trade</p>
            </div>
            <div className="market-actions-list">
              {sortedOutcomes.map((outcome) => {
                const price = getOutcomePrice(outcome.outcome_id);
                const priceDisplay = formatOutcomePrice(price);
                const probability = getOutcomeProbability(outcome.outcome_id);
                const binarySide: "yes" | "no" =
                  outcome.outcome_id === 0 ? "yes" : "no";
                const yesSellShares = getSellSharesForOutcome(
                  outcome.outcome_id,
                  "yes",
                );
                const noSellShares = getSellSharesForOutcome(
                  outcome.outcome_id,
                  "no",
                );

                return (
                  <div key={outcome.outcome_id} className="market-action-row">
                    <div className="market-action-row-header">
                      <span className="market-action-outcome">
                        {outcome.name}
                      </span>
                      <span className="market-action-price">
                        {priceDisplay}
                      </span>
                    </div>
                    <div className="market-action-probability-track">
                      <span
                        className="market-action-probability-fill"
                        style={{ width: `${probability}%` }}
                      />
                    </div>
                    <div className="market-action-buttons">
                      {outcomes.length === 2 ? (
                        <>
                          <button
                            // className="market-action-buy-button"
                            className={`market-action-buy-button ${
                              outcome.outcome_id === 0 ? "yes" : "no"
                            }`}
                            onClick={() =>
                              handleBuyForOutcome(outcome.outcome_id)
                            }
                            disabled={!session || market.resolved || isExpired}
                            type="button"
                          >
                            Buy {outcome.name} at {priceDisplay}
                          </button>
                          {yesSellShares > 0 && (
                            <button
                              className="market-action-sell-button"
                              onClick={() =>
                                handleOpenLmsrSellModal(binarySide)
                              }
                              disabled={market.resolved || isExpired}
                              type="button"
                            >
                              Sell {formatSellQuantity(yesSellShares)}
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            className="market-action-buy-button yes"
                            onClick={() =>
                              handleOutcomeButtonClick(
                                outcome.outcome_id,
                                "yes",
                              )
                            }
                            disabled={!session || market.resolved || isExpired}
                            type="button"
                          >
                            Buy Yes
                          </button>
                          <button
                            className="market-action-buy-button no"
                            onClick={() =>
                              handleOutcomeButtonClick(
                                outcome.outcome_id,
                                "no",
                              )
                            }
                            disabled={!session || market.resolved || isExpired}
                            type="button"
                          >
                            Buy No
                          </button>
                          {yesSellShares > 0 && (
                            <button
                              className="market-action-sell-button"
                              onClick={() =>
                                handleOpenLmsrSellModal(
                                  "yes",
                                  outcome.outcome_id,
                                )
                              }
                              disabled={market.resolved || isExpired}
                              type="button"
                            >
                              Sell Yes
                            </button>
                          )}
                          {noSellShares > 0 && (
                            <button
                              className="market-action-sell-button"
                              onClick={() =>
                                handleOpenLmsrSellModal(
                                  "no",
                                  outcome.outcome_id,
                                )
                              }
                              disabled={market.resolved || isExpired}
                              type="button"
                            >
                              Sell No
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* <section className="market-detail-card market-status-section">
            <div>
              <p className="market-section-eyebrow">Market Status</p>
            </div>
            <div className="market-status-options">
              {(["active", "expired", "resolved"] as const).map((status) => (
                <span
                  key={status}
                  className={`market-status-option ${status} ${marketStatus === status ? "selected" : ""}`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              ))}
            </div>
            {participants !== null && (
                    <span className="market-participants-detail">
                      <Users size={16} strokeWidth={2.25} />
                      {participants}{" "}
                      {participants === 1 ? "trader" : "traders"}
                    </span>
                  )}
            <div className="market-status-meta">
              <span>
                {getExpiryLabel(market.resolved, market.expireSec)}:{" "}
                {formatDate(market.expireSec, true)}
              </span>
              {resolvedOutcomeName && (
                <span>Outcome: {resolvedOutcomeName}</span>
              )}
            </div>
          </section> */}
        </div>
      </div>

      <div className="market-detail-tabs">
        <button
          className={activeTab === "trade" ? "active" : ""}
          onClick={() => setActiveTab("trade")}
        >
          Trade
        </button>
        <button
          className={activeTab === "comments" ? "active" : ""}
          onClick={() => setActiveTab("comments")}
        >
          Comments ({comments.length})
        </button>
        {/* <button
          className={activeTab === "activity" ? "active" : ""}
          onClick={() => setActiveTab("activity")}
        >
          Activity
        </button> */}
      </div>

      <div className="market-content">
        {activeTab === "activity" ? (
          <div className="activity-section">
            <div className="activity-filters">
              <div className="filter-group">
                <label>Event Type:</label>
                <select
                  value={activityFilter}
                  onChange={(e) => setActivityFilter(e.target.value)}
                >
                  <option value="">All Events</option>
                  <option value="createmkt">Market Created</option>
                  <option value="placeorder">Order Placed</option>
                  <option value="resolve">Market Resolved</option>
                </select>
              </div>
              <div className="filter-group">
                <label>User:</label>
                <input
                  type="text"
                  value={activityUserFilter}
                  onChange={(e) => setActivityUserFilter(e.target.value)}
                  placeholder="Filter by wallet address"
                />
              </div>
            </div>

            {activityLoading && activities.length === 0 ? (
              <Loader
                label="Loading activity..."
                className="app-loader-compact"
              />
            ) : (
              <div className="activity-list">
                {activities.length === 0 ? (
                  <p className="no-activity">
                    No activity found for this market.
                  </p>
                ) : (
                  activities.map((activity, index) => {
                    const activityTime = new Date(activity.timestamp);
                    const now = new Date();
                    const diffMs = now.getTime() - activityTime.getTime();
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMins / 60);
                    const diffDays = Math.floor(diffHours / 24);

                    let timeAgo = "";
                    if (diffDays > 0) {
                      timeAgo = `${diffDays}d ago`;
                    } else if (diffHours > 0) {
                      timeAgo = `${diffHours}h ago`;
                    } else if (diffMins > 0) {
                      timeAgo = `${diffMins}m ago`;
                    } else {
                      timeAgo = "Just now";
                    }

                    let activityDescription = "";
                    let activityClass = "";

                    switch (activity.type) {
                      case "createmkt":
                        activityDescription = `created this market`;
                        activityClass = "activity-create";
                        break;
                      case "placeorder":
                        const outcomeName =
                          outcomes.find(
                            (o) => o.outcome_id === activity.outcome_id,
                          )?.name || `Outcome ${activity.outcome_id}`;
                        const priceValue = activity.price
                          ? parseFloat(activity.price.split(" ")[0])
                          : 0;
                        activityDescription = `placed ${activity.side?.toUpperCase()} order for ${activity.quantity} shares of "${outcomeName}" at ${priceValue} ${TRADING_TOKEN}`;
                        activityClass =
                          activity.side === "buy"
                            ? "activity-buy"
                            : "activity-sell";
                        break;
                      case "lmsrbuy":
                        const lmsrOutcomeName =
                          outcomes.find(
                            (o) => o.outcome_id === activity.outcome_id,
                          )?.name || (activity.outcome_id === 0 ? "Yes" : "No");
                        activityDescription = `bought ${activity.quantity} of "${lmsrOutcomeName}" shares`;
                        activityClass = "activity-buy";
                        break;
                      case "resolve":
                        const resolvedOutcome =
                          outcomes.find(
                            (o) => o.outcome_id === activity.outcome,
                          )?.name || `Outcome ${activity.outcome}`;
                        activityDescription = `resolved market to "${resolvedOutcome}"`;
                        activityClass = "activity-resolve";
                        break;
                      default:
                        activityDescription = `performed ${activity.type}`;
                        activityClass = "activity-other";
                    }

                    return (
                      <div
                        key={`${activity.trx_id}-${index}`}
                        className={`activity-item ${activityClass}`}
                      >
                        <div className="activity-header">
                          <span className="activity-user">{activity.user}</span>
                          <span className="activity-time">{timeAgo}</span>
                        </div>
                        <div className="activity-description">
                          {activityDescription}
                        </div>
                        <div className="activity-meta">
                          <a
                            href={`https://explorer.xprnetwork.org/transaction/${activity.trx_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="activity-link"
                          >
                            View on XPR Explorer →
                          </a>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        ) : activeTab === "trade" ? (
          <>
            {showTradingGuide && (
              <div className="trading-guide-banner">
                <div className="trading-guide-content">
                  <h4>How Trading Works</h4>
                  <p>
                    Buy shares for the outcome you predict will win. Buying
                    shares is like betting on the outcome. Odds shift in real
                    time as other traders bet.
                  </p>
                  <p>
                    Sell your shares at any time, or wait until the market ends
                    to redeem winning shares for 1 ${TRADING_TOKEN} each.
                  </p>
                </div>
                <button
                  className="trading-guide-dismiss"
                  onClick={dismissTradingGuide}
                >
                  Got it
                </button>
              </div>
            )}

            {/* V2 binary position display */}
            {session &&
              market &&
              market.version !== 3 &&
              lmsrPosition &&
              (lmsrPosition.sharesYes > 0 || lmsrPosition.sharesNo > 0) && (
                <div className="my-position">
                  <div>
                  <h3>
                    My Position
                    <Tooltip
                      text="Your current share holdings in this market. Winning shares can be redeemed for 1 XUSDC each when the market resolves."
                      position="right"
                    >
                    <span className="pos-info-icon">    
                                      <Info size={16}/>
                    </span>
                    </Tooltip>
                  </h3>
                  </div>
                  <div className="position-details">
                    {lmsrPosition.sharesYes > 0 && (
                      <div className="position-row yes">
                        <span className="position-outcome">
                          {outcomes.find((o) => o.outcome_id === 0)?.name ||
                            "Yes"}
                        </span>
                        <span className="position-shares-with-sell">
                          <span>
                            {lmsrPosition.sharesYes.toFixed(2)} shares
                          </span>
                          <button
                            className="position-sell-btn"
                            onClick={() => handleOpenLmsrSellModal("yes")}
                            disabled={market.resolved || isExpired}
                          >
                            Sell
                          </button>
                        </span>
                      </div>
                    )}
                    {lmsrPosition.sharesNo > 0 && (
                      <div className="position-row no">
                        <span className="position-outcome">
                          {outcomes.find((o) => o.outcome_id === 1)?.name ||
                            "No"}
                        </span>
                        <span className="position-shares-with-sell">
                          <span>{lmsrPosition.sharesNo.toFixed(2)} shares</span>
                          <button
                            className="position-sell-btn"
                            onClick={() => handleOpenLmsrSellModal("no")}
                            disabled={market.resolved || isExpired}
                          >
                            Sell
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    className="position-note"
                    style={{
                      fontSize: "0.85em",
                      color: "#aaa",
                      marginTop: "8px",
                    }}
                  >
                    Winning shares pay 1 XUSDC each when the market resolves.
                    Claim your winnings from the Portfolio page after
                    resolution.
                  </p>
                </div>
              )}

            {/* V3 per-outcome position display */}
            {session &&
              market &&
              market.version === 3 &&
              lmsrPositionsV3 &&
              Object.keys(lmsrPositionsV3).length > 0 && (
                <div className="my-position">
                  <h3>
                    My Positions
                    <Tooltip
                      text="Your share holdings per outcome. YES shares pay 1 XUSDC if the outcome wins. NO shares pay 1 XUSDC if the outcome loses."
                      position="right"
                    >
                      <span className="tooltip-icon">ℹ</span>
                    </Tooltip>
                  </h3>
                  {multiOutcomeInvested > 0 && (
                    <p className="position-note">
                      Invested: {multiOutcomeInvested} ${TRADING_TOKEN}
                    </p>
                  )}
                  <div className="position-details">
                    {outcomes.map((outcome) => {
                      const pos = lmsrPositionsV3[outcome.outcome_id];
                      if (!pos || (pos.sharesYes <= 0 && pos.sharesNo <= 0))
                        return null;
                      return (
                        <div
                          key={outcome.outcome_id}
                          className="position-outcome-group"
                        >
                          <div className="position-outcome-header">
                            {outcome.name}
                          </div>
                          {pos.sharesYes > 0 && (
                            <div className="position-row yes">
                              <span className="position-outcome">YES</span>
                              <span className="position-shares-with-sell">
                                <span>{pos.sharesYes.toFixed(2)} shares</span>
                                <button
                                  className="position-sell-btn"
                                  onClick={() =>
                                    handleOpenLmsrSellModal(
                                      "yes",
                                      outcome.outcome_id,
                                    )
                                  }
                                  disabled={market.resolved || isExpired}
                                >
                                  Sell
                                </button>
                              </span>
                            </div>
                          )}
                          {pos.sharesNo > 0 && (
                            <div className="position-row no">
                              <span className="position-outcome">NO</span>
                              <span className="position-shares-with-sell">
                                <span>{pos.sharesNo.toFixed(2)} shares</span>
                                <button
                                  className="position-sell-btn"
                                  onClick={() =>
                                    handleOpenLmsrSellModal(
                                      "no",
                                      outcome.outcome_id,
                                    )
                                  }
                                  disabled={market.resolved || isExpired}
                                >
                                  Sell
                                </button>
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p
                    className="position-note"
                    style={{
                      fontSize: "0.85em",
                      color: "#aaa",
                      marginTop: "8px",
                    }}
                  >
                    YES shares pay 1 XUSDC each if the outcome wins. NO shares
                    pay 1 XUSDC each if the outcome loses. Claim your winnings
                    from the Portfolio page after resolution.
                  </p>
                </div>
              )}

            {relatedMarkets.length > 0 && (
              <div className="related-markets">
                <h3>Related Markets</h3>
                <div className="related-markets-grid">
                  {relatedMarkets.map((rm: any) => (
                    <div
                      key={rm.market_id}
                      className="related-market-card"
                      onClick={() => {
                        if (onSelectRelatedMarket) {
                          onSelectRelatedMarket(rm.market_id);
                        }
                      }}
                    >
                      <div className="related-market-category">
                        {rm.category}
                      </div>
                      <div className="related-market-question">
                        {rm.question}
                      </div>
                      <div className="related-market-expiry">
                         {formatDate(normalizeTimestamp(rm.expire))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {session && myOrders.length > 0 && (
              <div className="my-orders">
                <h3>
                  My Orders (All Outcomes)
                  <Tooltip
                    text="All your active orders in this market across all outcomes. Click cancel to remove an order."
                    position="right"
                  >
                    <span className="tooltip-icon">ℹ</span>
                  </Tooltip>
                </h3>
                <div className="my-orders-list">
                  {Object.entries(myOrdersByOutcome).map(
                    ([outcomeId, outcomeOrders]) => {
                      const outcome = outcomes.find(
                        (o) => o.outcome_id === parseInt(outcomeId),
                      );
                      return (
                        <div key={outcomeId} className="outcome-orders">
                          <h4>{outcome?.name || `Outcome ${outcomeId}`}</h4>
                          {outcomeOrders.map((order) => (
                            <div key={order.order_id} className="my-order-row">
                              <span
                                className={`order-side ${order.isBid ? "bid" : "ask"}`}
                              >
                                {order.isBid ? "BUY" : "SELL"}
                              </span>
                              <span className="order-price">
                                {order.price} ${TRADING_TOKEN}
                              </span>
                              <span className="order-quantity">
                                ×{order.quantity}
                              </span>
                              <div className="order-actions">
                                {order.isBid && (
                                  <button
                                    onClick={() => handleOpenSellModal(order)}
                                    className="sell-button"
                                  >
                                    Sell
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    handleCancelOrder(order.order_id)
                                  }
                                  className="cancel-button"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}

            {/* Only show order book for legacy markets (version < 2). LMSR markets use automated market maker. */}
            {market && market.version < 2 && (
              <div className="order-book">
                <h3>
                  Order Book (Selected Outcome)
                  <Tooltip
                    text="The order book shows all active buy (bids) and sell (asks) orders for the currently selected outcome. Orders are matched automatically when prices meet."
                    position="right"
                  >
                    <span className="tooltip-icon">ℹ</span>
                  </Tooltip>
                </h3>
                <div className="order-book-grid">
                  <div className="bids">
                    <h4>
                      Bids (Buy)
                      <Tooltip
                        text="Buy orders for 'Yes' shares. Higher prices are shown first."
                        position="right"
                      >
                        <span className="tooltip-icon">ℹ</span>
                      </Tooltip>
                    </h4>
                    <div className="order-list">
                      {bids.length === 0 ? (
                        <div className="no-orders">No bids</div>
                      ) : (
                        (() => {
                          const maxQty = Math.max(
                            ...bids.map((o) => o.quantity),
                          );
                          return bids.map((order) => (
                            <div
                              key={order.order_id}
                              className="order-row"
                              style={
                                {
                                  "--depth-width": `${(order.quantity / maxQty) * 100}%`,
                                } as React.CSSProperties
                              }
                            >
                              <span className="price">
                                {order.price} ${TRADING_TOKEN}
                              </span>
                              <span className="quantity">{order.quantity}</span>
                            </div>
                          ));
                        })()
                      )}
                    </div>
                  </div>
                  <div className="asks">
                    <h4>
                      Asks (Sell)
                      <Tooltip
                        text="Sell orders for 'Yes' shares. Lower prices are shown first."
                        position="right"
                      >
                        <span className="tooltip-icon">ℹ</span>
                      </Tooltip>
                    </h4>
                    <div className="order-list">
                      {asks.length === 0 ? (
                        <div className="no-orders">No asks</div>
                      ) : (
                        (() => {
                          const maxQty = Math.max(
                            ...asks.map((o) => o.quantity),
                          );
                          return asks.map((order) => (
                            <div
                              key={order.order_id}
                              className="order-row"
                              style={
                                {
                                  "--depth-width": `${(order.quantity / maxQty) * 100}%`,
                                } as React.CSSProperties
                              }
                            >
                              <span className="price">
                                {order.price} ${TRADING_TOKEN}
                              </span>
                              <span className="quantity">{order.quantity}</span>
                            </div>
                          ));
                        })()
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="comments-section">
            <h3>Discussion</h3>

            {session ? (
              <form onSubmit={handlePostComment} className="comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts on this market..."
                  maxLength={1000}
                  rows={3}
                  disabled={commentLoading}
                />
                <div className="comment-form-footer">
                  <span className="char-count">{newComment.length}/1000</span>
                  <button
                    type="submit"
                    disabled={commentLoading || !newComment.trim()}
                  >
                    {commentLoading ? "Posting..." : "Post Comment"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="login-prompt">
                <p>Connect your wallet to join the discussion</p>
              </div>
            )}

            {replyingTo && (
              <div className="replying-to-banner">
                <span>Replying to {replyingTo.user_account}</span>
                <button
                  onClick={() => {
                    setReplyingTo(null);
                    setNewComment("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">
                  No comments yet. Be the first to share your thoughts!
                </p>
              ) : (
                comments
                  .filter((c) => !c.parent_comment_id)
                  .map((comment) => (
                    <div key={comment.id}>
                      <div
                        className={`comment-card ${comment.is_deleted ? "deleted" : ""}`}
                      >
                        <div className="comment-header">
                          <span className="comment-author">
                            {comment.user_account}
                          </span>
                          <span className="comment-time">
                            {new Date(
                              comment.created_at * 1000,
                            ).toLocaleString()}
                          </span>
                        </div>
                        {renderCommentText(comment)}
                        {!comment.is_deleted && (
                          <div className="comment-actions">
                            <button
                              onClick={() => handleReply(comment)}
                              className="reply-button"
                            >
                              Reply
                            </button>
                            {session &&
                              commentAdmins.includes(
                                session.auth.actor.toString(),
                              ) && (
                                <button
                                  onClick={() =>
                                    handleDeleteComment(comment.id)
                                  }
                                  className="delete-button"
                                >
                                  Delete
                                </button>
                              )}
                          </div>
                        )}
                        <CommentReactions
                          commentId={comment.id}
                          marketId={marketId}
                          session={session}
                          reactions={
                            commentReactions[comment.id] || DEFAULT_REACTIONS
                          }
                          userReaction={
                            userCommentReactions[comment.id] || null
                          }
                          onReactionChange={fetchCommentReactions}
                        />
                      </div>

                      {comments
                        .filter((r) => r.parent_comment_id === comment.id)
                        .map((reply) => (
                          <div
                            key={reply.id}
                            className={`comment-card reply ${reply.is_deleted ? "deleted" : ""}`}
                          >
                            <div className="comment-header">
                              <span className="comment-author">
                                {reply.user_account}
                              </span>
                              <span className="comment-time">
                                {new Date(
                                  reply.created_at * 1000,
                                ).toLocaleString()}
                              </span>
                            </div>
                            {renderCommentText(reply)}
                            {!reply.is_deleted && (
                              <div className="comment-actions">
                                {session &&
                                  commentAdmins.includes(
                                    session.auth.actor.toString(),
                                  ) && (
                                    <button
                                      onClick={() =>
                                        handleDeleteComment(reply.id)
                                      }
                                      className="delete-button"
                                    >
                                      Delete
                                    </button>
                                  )}
                              </div>
                            )}
                            <CommentReactions
                              commentId={reply.id}
                              marketId={marketId}
                              session={session}
                              reactions={
                                commentReactions[reply.id] || DEFAULT_REACTIONS
                              }
                              userReaction={
                                userCommentReactions[reply.id] || null
                              }
                              onReactionChange={fetchCommentReactions}
                            />
                          </div>
                        ))}
                    </div>
                  ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketDetail;
